import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Icon } from "../FormUI/FormUI";
import { MessageButtonType, MessageType, MessageWindow } from "../MessageWindow";
import { FormProfileItem } from "../XGControls/Forms/FormProfileItem";
import { FormProfileManager } from "../XGControls/Forms/FormProfileManager";
import { FormProfileDetailBuilder, IProfileData } from "./FormProfileDetailBuilder";
import { IContentBuilder } from "./IContentBuilder";
import { LocalizedStringBuilder } from "./LocalizedStringBuilder";

/** Prvek, umožňující provádět akce s profilem */
export class FormProfileItemBuilder implements IContentBuilder {
    //#region Fields
    private readonly _element: HTMLElement;
    private readonly _iconElement: HTMLElement;
    private readonly _textElement: HTMLElement;
    private readonly _titleElement: HTMLElement;
    private readonly _buttonsElement: HTMLElement;
    private readonly _editButton: HTMLElement;
    private _editElement: HTMLElement;
    private _defaultHintElement: HTMLElement;

    private readonly _profileManager: FormProfileManager;

    private _name: string;
    private _selected: boolean;
    private _isDefault: boolean = false;
    private _showInNavigationTree: boolean;
    /** Zda se má zabránit načtení dat po otevření formu */
    private _queryMode: boolean | null;
    private _isEdited: boolean = false;
    private _showMenuActions: boolean;
    /** Příznak, zda jde o "Základní" profil */
    private readonly _isBasicProfile: boolean = false;
    private _storeFilters: boolean = true;
    private _isImplementation: boolean = false;

    private _saveFunction: (result: IProfileData) => Promise<void>;
    private readonly _cancelFunction: () => void;
    //#endregion Fields

    //#region Properties
    public get title(): string {
        return this._titleElement.textContent;
    }

    public set title(value: string) {
        this._titleElement.textContent = value;
        this._titleElement.title = value;
        if (this._isImplementation && this._profileManager.canEditImplementationProfiles) {
            const impTitle = createElement({ type: "span" });
            Localization.addLocData(impTitle, "GUI-740260"); // [IMP]
            this._titleElement.insertAdjacentElement("beforeend", impTitle);
        }
    }

    public set selected(value: boolean) {
        this._selected = value;
        this._iconElement.classList.toggle(Icon[Icon.selectFromLov], value);
    }

    public get selected(): boolean {
        return this._selected;
    }

    public set isDefault(value: boolean) {
        if (this._isDefault === value) {
            return;
        }

        this._isDefault = value;

        if (value) {
            if (!this._defaultHintElement) {
                this._defaultHintElement = createElement({ classes: "default-text" });
                Localization.addLocData(this._defaultHintElement, "GUI-454139"); // Výchozí
            }
            this._textElement.appendChild(this._defaultHintElement);

            if (this._isBasicProfile) {
                this._buttonsElement.removeChild(this._editButton);
            }

            return;
        } else {
            this._defaultHintElement?.remove();
        }

        if (this._isBasicProfile) {
            this._buttonsElement.appendChild(this._editButton);
        }
    }

    public get isDefault(): boolean {
        return this._isDefault;
    }

    public get isEdited(): boolean {
        return this._isEdited;
    }

    public set isEdited(value: boolean) { // Pro externí změnu stavu
        this._setEditMode(value);
    }
    //#endregion Properties

    constructor(profileManager: FormProfileManager, profile: FormProfileItem) {
        this._profileManager = profileManager;

        this._element = createElement({ classes: "form-profile-item" });
        this._iconElement = createElement({ classes: "icon" });
        this._textElement = createElement({ classes: "text" });
        this._titleElement = createElement({ classes: "title" });
        this._textElement.appendChild(this._titleElement);

        this._buttonsElement = createElement({ classes: "buttons" });

        this._element.appendChild(this._iconElement);
        this._element.appendChild(this._textElement);
        this._element.appendChild(this._buttonsElement);

        if (!profile.isImplementation || profileManager.canEditImplementationProfiles) {
            this._editButton = createElement({ type: "button", classes: "EditButton flat icon edit btn no-caption" });
            Localization.addLocData(this._editButton, "GUI-665724", "title", ""); // Upravit zobrazení; Popisek tlačítka na úpravu zobrazení (profilu)
            this._buttonsElement.appendChild(this._editButton);

            this._editButton.addEventListener("click", async (e: MouseEvent) => {
                if (this._editButton.classList.contains("Disabled"))
                    return;
                if (e.button === 0) { // Levé tlačítko myši
                    this._profileManager.profileEdited.trigger(this._profileManager, profile);
                    this._setEditMode(true, this._isBasicProfile);
                }
            });

            if (profile.id) {
                const removeButton = createElement({ type: "button", classes: "RemoveButton flat icon editorial-trash btn no-caption" });
                Localization.addLocData(removeButton, "GUI-624411", "title", ""); // Odstranit zobrazení; Popisek tlačítka na smazání zobrazení (profilu)
                this._buttonsElement.appendChild(removeButton);

                removeButton.addEventListener("click", async (e: MouseEvent) => {
                    if (e.button !== 0 || this._editButton.classList.contains("Disabled")) { // Jiné než levé tlačítko myši
                        return;
                    }

                    EventHelper.stopImmediatePropagation(e);

                    const confirmDialog = new MessageWindow(new LocalizedStringBuilder("GUI-599180")); // Odstranit?
                    confirmDialog.okButtonCaption = "GUI-146838"; // "Ano"
                    confirmDialog.noButtonCaption = "GUI-146840"; // "Ne"
                    confirmDialog.type = MessageType.message;
                    confirmDialog.show().then((r) => {
                        if (r.result === MessageButtonType.ok) {
                            this._profileManager.removeProfile(profile);
                        }
                    });
                });
            }
        }

        this._isBasicProfile = profile.id == null;
        this._isImplementation = profile.isImplementation;
        this._name = profile.title;
        this.title = profile.title;
        this.selected = profile.selected;
        this.isDefault = profile.default;
        this._showInNavigationTree = profile.showInNavigationTree;
        this._queryMode = profile.queryMode;
        this._storeFilters = profile.storeFilters;

        this._saveFunction = async (result: IProfileData) => {
            const wasDefaultChangedToFalse = (profile.default && !result.isDefault);
            profile.isImplementation = result.isImplementation;
            profile.title = result.name;
            profile.default = result.isDefault;
            profile.showInNavigationTree = result.showInNavigationTree;
            profile.queryMode = result.queryMode;
            profile.storeFilters = result.storeFilters;

            if (await this._profileManager.saveCurrentProfile(profile)) {
                profile.title = result.name;
                this._isImplementation = profile.isImplementation;
                this.title = this._name = profile.title;
                this.isDefault = profile.default;
                this._showInNavigationTree = profile.showInNavigationTree;
                this._queryMode = profile.queryMode;
                this._storeFilters = profile.storeFilters;
                const defaultImplementationProfile = this._profileManager.defaultImplementationProfile;
                if (wasDefaultChangedToFalse) {
                    if (defaultImplementationProfile) {
                        // Pokud existuje defaultní implementační profil, nastaví se jako výchozí.
                        defaultImplementationProfile.default = true;
                    } else {
                        // V opačném případě se nastaví "Základní" profil jako výchozí, aby nedošlo k situaci,
                        // že nebude žádný z profilů označen jako výchozí".
                        this._profileManager.setBasicProfileDefault();
                    }
                }
            }

            if (profile.basic) { // U základního profilu se jen resetuje stav, nespouští se uložení ani události změny
                this._setEditMode(false);
            }
        };

        this._cancelFunction = () => this._profileManager.reloadActualProfile();

        profile.setSidePanelItem(this);

        this._element.addEventListener("click", () => profile.selected = true);

        this._profileManager.profileEdited.add((_profileManager, profileItem) => { // Zruší editaci na všech profilech mimo ten, na který se kliklo
            if (!profile.isDisposed()) { // TODO Provizorní kontrola na disposed profile. Správné odregistrování tohoto posluchače, aby nepracoval s disposed položkami bude řešeno v tasku 34324
                if (this.isEdited && (profileItem && profileItem.id !== profile.id) || !profileItem) {
                    profile.discardChanges();
                }
            }
        });
    }

    //#region Methods
    /** Vrátí vytvořený element */
    public async build(): Promise<HTMLElement> {
        return this._element;
    }

    /**
     * Aktualizuje viditelnost akcí spojených s menu
     * @param visible Zda mají být viditelné
     */
    public updateMenuActionsVisibility(visible: boolean): void {
        this._showMenuActions = visible;
    }

    /** Odstraní sebe sama */
    public remove(): void {
        this._element.remove();
    }

    /** Scroll sám na sebe */
    public scrollIntoView(): void {
        this._element.scrollIntoView(false);
    }

    /**
     * Změní editační stav profilu (interně)
     * @param value Zda má přejít do editace
     */
    private async _setEditMode(value: boolean, basicWithDetail?: boolean): Promise<void> {
        if (this._isEdited === value) {
            return;
        }

        this._isEdited = value;

        if (this._isBasicProfile && !this._editElement && !basicWithDetail) { // Pouze pro externě modifikovaný základní profil
            this._setBasicEditMode(value);

            return;
        }

        this._element.classList.toggle("edited");

        if (value) {
            this._element.removeChild(this._textElement);
            this._element.removeChild(this._buttonsElement);

            const formProfileDetailBuilder = new FormProfileDetailBuilder({
                profileManager: this._profileManager,
                isDefault: this.isDefault,
                name: this._name,
                queryMode: this._queryMode,
                showInNavigationTree: this._showInNavigationTree,
                showMenuActions: this._showMenuActions,
                isBasicProfile: this._isBasicProfile,
                storeFilters: this._storeFilters,
                isImplementation: this._isImplementation,
                saveFunction: this._saveFunction,
                cancelFunction: this._cancelFunction,
            });

            this._editElement = await formProfileDetailBuilder.build();
            this._element.appendChild(this._editElement);

            return;
        }
        // Reset titulku na aktuální hodnotu - ať už uloženou nebo původní při zrušení změn
        this.title = this._name;

        // Vyhodí editační prvky
        this._element.removeChild(this._editElement);
        this._editElement = null;

        // Vrátí zpět informační prvky
        this._element.appendChild(this._textElement);
        this._element.appendChild(this._buttonsElement);
    }

    /**
     * Změní editační stav výchozího profilu (bez editačního detailu)
     * @param value Zda má přejít do editace
     */
    private _setBasicEditMode(value: boolean): void {
        if (value) {
            const editedTitle = createElement({ type: "span", classes: "edited-profile-caption" });
            Localization.addLocData(editedTitle, "GUI-624409"); // Upraveno; Obsah závorky upraveného zobrazení (profilu)
            this._titleElement.insertAdjacentElement("beforeend", editedTitle);

            if (!this.isDefault) {
                this._buttonsElement.removeChild(this._editButton);
            }

            this._buttonsElement.appendChild(FormProfileDetailBuilder.createCancelButton(this._cancelFunction));

            return;
        }

        this._titleElement.lastChild.remove();
        this._buttonsElement.lastChild.remove();

        if (!this.isDefault) {
            this._buttonsElement.appendChild(this._editButton);
        }
    }
    //#endregion Methods
}
